/**
 * The Console's binding of the OnMarble launcher (#2328 §3/§4, B3 ruling).
 *
 * ── THE CONSOLE IS ALWAYS `actions`, AND IT READS NO SETTING TO BE ────────
 * §4: *"Console: always `actions`; it cannot be configured away."* That is held
 * by this HOST rather than by the experience cascade, and the reason is a
 * measurement rather than a preference: `core.setting` is `primary key (module,
 * key)` with no persona, host or tenant axis, so it cannot express a per-surface
 * override at all; and `core.experience` — which does have precedence — puts a
 * module's registration at rung `system` (rank 20) while `tenant` is 50 and
 * `persona` is 30. A launcher mode expressed there could therefore be
 * configured away by exactly the tenant §4 forbids. So the console passes the
 * literal, and `ConsoleLauncher.test.tsx` pins that it has no other branch.
 *
 * ── the About panel is the kit's, and the FACTS are this host's ───────────
 * `AboutOnMarblePanel` renders what it is handed and omits what it is not. The
 * console can source the deployment's own name and instance type, and it reads
 * them through the SAME `['environment-health']` query key the navigation shell
 * already uses — react-query dedupes on the key, so this adds a consumer and
 * not a second poll of `core.deployment_identity()`.
 *
 * MEASURED and therefore ABSENT: there is no product version/build door in this
 * tree, and no registered legal or privacy destination. Neither is invented
 * here; the panel simply has no such row.
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { AboutOnMarblePanel, OnMarbleLauncher, type AboutFact, type OnMarbleLauncherPlacement } from '@solum/ui'
import { useSolum } from '../data/client'
import { useMyAreas } from '../access/access'
import { loadEnvironmentHealth } from '../ops/setupLive'
import { backstageAdmitted, consoleLauncherActions } from './consoleLauncherActions'
import { rememberSettingsReturn } from '../settings/returnRoute'

/**
 * `placement` is where the ONE mount sits, never whether there is one: the
 * Console passes `cap` from the dock (#2406), so the trigger is the right-edge
 * wall's top course and the top bar carries no launcher.
 */
export function ConsoleLauncher({ placement = 'cap' }: { placement?: OnMarbleLauncherPlacement } = {}) {
  const navigate = useNavigate()
  const { client, isLive } = useSolum()
  const [about, setAbout] = useState(false)
  const areas = useMyAreas()
  /* The gate the shell ALREADY enforces for `/settings` — see the header of
     `consoleLauncherActions.ts` for why this is the backstage area and not a
     `platform.admin` door. Defaults to admitted while the read is in flight,
     the way `Nav` does, so staff do not watch a tile appear a beat late; the
     route itself is server-authorized either way (§8). */
  const mayReachPlatformSettings = backstageAdmitted(areas.data)

  const environment = useQuery({
    queryKey: ['environment-health'], enabled: isLive && about,
    queryFn: () => loadEnvironmentHealth(client),
  })
  const identity = environment.data?.identity
  const facts: AboutFact[] = []
  if (identity?.name) facts.push({ label: 'Deployment', value: identity.name })
  if (identity?.instance_type) facts.push({ label: 'Environment', value: identity.instance_type })

  return (
    <>
      <OnMarbleLauncher
        surface="console"
        placement={placement}
        mode="actions"
        actions={consoleLauncherActions({
          go: (to) => {
            if (to === '/settings') rememberSettingsReturn()
            void navigate({ to })
          },
          onAbout: () => setAbout(true),
          mayReachPlatformSettings,
        })}
      />
      {about ? <AboutOnMarblePanel facts={facts} onClose={() => setAbout(false)} /> : null}
    </>
  )
}
